import React from 'react'
import { PageProps, graphql } from 'gatsby'
import { Trans, useTranslation } from 'react-i18next'
import { ProIndexQuery } from '@graphql-types'
import PROLayout from './_layout'
import { GatsbyImage } from 'gatsby-plugin-image'
import {
	FaCalendar,
	FaCalendarCheck,
	FaClock,
	FaMapMarkerAlt,
	FaPortrait,
	FaStar,
} from 'react-icons/fa'
import ReactMarkdown from 'react-markdown'
import moment from 'moment'
import { cx } from '@emotion/css'
import CourseButtons from '@components/CourseButtons'
import { css, useTheme } from '@emotion/react'
import FeaturedBy from '@components/FeaturedBy'
import CoursePRONav from './_nav'
import CourseSummary from '@components/CourseSummary'
import { maxWidth, minWidth, useNumberFormatters } from 'src/themes'
import CourseCurriculum from '@components/CourseCurriculum'
import { H2Highlight } from '@components/H2Highlight'
import StaffComponent from '@components/StaffComponent'
import { StaffWrap } from '@components/StaffWrap'

const ProIndexPage = (props: PageProps<ProIndexQuery>) => {
	const { t } = useTranslation()
	const theme = useTheme()
	const today = moment()
	const numberFormatters = useNumberFormatters()

	const course = props.data.course!
	const nextCohort = course.cohorts?.find((cohort) =>
		moment(
			cohort?.value?.dates?.[0]?.value ?? cohort?.value?.start_date,
			'YYYY-MM-DD'
		).isAfter(today)
	)

	return (
		<PROLayout course={course}>
			<GatsbyImage
				css={css`
					margin: 2rem;
					${minWidth[2]} {
						display: none;
					}
				`}
				image={course.imageSharp?.childImageSharp?.gatsbyImageData}
				alt={course.name}
			/>
			<div
				css={css`
					display: flex;
					gap: 3rem;

					> div:first-of-type {
						order: 1;
						min-width: 300px;
					}

					${maxWidth[2]} {
						gap: 2rem;
						flex-direction: column;

						> div:first-of-type {
							order: 0;
						}
					}
				`}>
				<div>
					<h2>{t('courses:common.upcoming_cohorts')}</h2>
					{course.cohorts
						?.filter((val) =>
							moment(
								val?.value?.dates?.[0]?.value ??
									val?.value?.start_date,
								'YYYY-MM-DD'
							).isAfter(today)
						)
						.map((cohort, i) => (
							<div
								key={i}
								css={css`
									display: flex;
									max-width: 700px;
									margin: 1rem auto;
									font-size: 24px;
									color: #fff;
									padding-bottom: 0.5rem;
									border-bottom: solid 1px
										rgba(255, 255, 255, 0.2);

									> span {
										display: block;
										color: white;

										&:last-child {
											flex: 1;
										}
									}
								`}>
								<h3
									css={css`
										display: flex;
										flex-direction: column;
										text-align: center;
										padding: 0 1.5rem;
										font-size: 2rem;
										margin: 0;

										small {
											font-size: 0.8rem;
											font-weight: normal;
										}
									`}>
									<Trans
										i18nKey='courses:common.cohort_x'
										values={{
											cohort: cohort?.value?.cohort!,
										}}>
										<small>Text</small> Text
									</Trans>
								</h3>
								<dl
									css={css`
										margin: 0;

										> dt,
										> dd {
											margin: 0;
										}

										> dt {
											font-size: 0.8rem;
											margin-top: 0.5rem;

											&:first-of-type {
												margin: 0;
											}
										}
									`}>
									<dt>{t('courses:common.start_date')}</dt>
									<dd>
										{cohort?.value?.dates?.[0]?.value ??
											cohort?.value?.start_date}{' '}
										(
										{moment(
											cohort?.value?.dates?.[0]?.value ??
												cohort?.value?.start_date,
											'YYYY-MM-DD'
										).format('ddd')}
										)
									</dd>

									{cohort?.value?.dates &&
										cohort?.value?.dates.length > 0 && (
											<>
												<dt>
													{t(
														'courses:common.all_dates'
													)}
												</dt>
												<dd
													css={css`
														p {
															margin: 0.2rem 0;
															font-size: 1rem;
															line-height: 1rem;
														}
													`}>
													{cohort?.value?.dates?.map(
														(date) => (
															<p>
																{date?.value} (
																{moment(
																	date?.value,
																	'YYYY-MM-DD'
																).format('ddd')}
																)
															</p>
														)
													)}
												</dd>
											</>
										)}
									{cohort?.value?.price && (
										<>
											<dt>{t('courses:common.price')}</dt>
											<dd
												css={css`
													p {
														margin: 0.2rem 0;
														font-size: 1rem;
														line-height: 1rem;
													}
												`}>
												{numberFormatters['hkd'].format(
													parseFloat(
														cohort.value.price
													)
												)}
											</dd>
										</>
									)}
									<dt>{t('courses:common.venue')}</dt>
									<dd>
										{
											// For the purpose of extract-i18n:
											false &&
												t('courses:common.tsuen_wan') &&
												t(
													'courses:common.sheung_wan'
												) &&
												t(
													'courses:common.kowloon_bay'
												) &&
												t('courses:common.uk_online')
										}
										{[
											'tsuen_wan' as const,
											'sheung_wan' as const,
											'kowloon_bay' as const,
											'uk_online' as const,
										].map(
											(location) =>
												cohort?.value?.[location] && (
													<span
														className={cx({
															full: cohort
																.value?.[
																`${location}_full`
															],
															almost: cohort
																.value?.[
																`${location}_almost`
															],
														})}>
														{location.startsWith(
															'uk'
														)
															? '🇬🇧'
															: '🇭🇰'}{' '}
														{t(
															`courses:common.${location}`
														)}{' '}
														{cohort.value?.[
															`${location}_full`
														] &&
															`(${t(
																'courses:common.full'
															)})`}{' '}
														{cohort.value?.[
															`${location}_almost`
														] &&
															`(${t(
																'courses:common.almost_full'
															)})`}
													</span>
												)
										)}
									</dd>
								</dl>
							</div>
						))}
				</div>
				<div>
					<div>
						<ReactMarkdown children={course.description!} />
					</div>
				</div>
			</div>
			<CourseButtons course={course} />
			<div
				css={css`
					align-self: stretch;
				`}>
				<H2Highlight>{t('courses:common.curriculum')}</H2Highlight>
				{course.details_simple && (
					<div>
						<div>
							<ReactMarkdown children={course.details_simple} />
						</div>
					</div>
				)}
				{course.details?.map(
					(detail, i) =>
						detail && (
							<CourseCurriculum
								key={i}
								noImage={
									detail.imageSharp?.childImageSharp
										?.gatsbyImageData == null
								}>
								{detail.imageSharp?.childImageSharp
									?.gatsbyImageData && (
									<div>
										<GatsbyImage
											image={
												detail.imageSharp
													?.childImageSharp
													?.gatsbyImageData
											}
											alt={detail.title!}
										/>
									</div>
								)}
								<div>
									<h3>{detail.title}</h3>
									<small>{detail.mode}</small>
									<ReactMarkdown
										children={detail.description!}
									/>
									<div className='logos'>
										{detail.iconsSharp?.map((icon, j) => (
											<GatsbyImage
												image={
													icon.childImageSharp
														?.gatsbyImageData
												}
												alt={
													detail.icons?.[j]?.meta
														?.title
												}
											/>
										))}
									</div>
								</div>
							</CourseCurriculum>
						)
				)}
			</div>
			{/* {isTQUK !== '' ? <div css={css`
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 1rem;
						margin: 1rem;
					`}>
				<div
					css={css`
						display: flex;
						width: 50%;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
				<GatsbyImage
					image={
						props.data.course?.Tecky_Academy_MicroMaster_CertificateSharp?.childImageSharp
							?.gatsbyImageData
					}
					alt={''}
				/>
			</div>
			<div
					css={css`
						display: flex;
						width: 50%;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
				<GatsbyImage
					image={
						props.data.course?.TQUK_CertificateSharp?.childImageSharp
							?.gatsbyImageData
					}
					alt={''}
				/>
			</div>
			</div> : <div
					css={css`
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
				<GatsbyImage
					image={
						props.data.course?.Tecky_Academy_MicroMaster_CertificateSharp?.childImageSharp
							?.gatsbyImageData
					}
					alt={''}
				/>
			</div>
			} */}
			<div
					css={css`
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
				<GatsbyImage
					image={
						props.data.course?.Tecky_Academy_MicroMaster_CertificateSharp?.childImageSharp
							?.gatsbyImageData
					}
					alt={props.data.course?.Tecky_Academy_MicroMaster_Certificate?.meta?.title}
				/>
			</div>
			<div
					css={css`
						display: flex;
						align-items: center;
						justify-content: center;
						flex-wrap: wrap;
						gap: 1rem;
						margin: 1rem;
					`}>
				<GatsbyImage
					image={
						props.data.course?.TQUK_CertificateSharp?.childImageSharp
							?.gatsbyImageData
					}
					alt={props.data.course?.TQUK_Certificate?.meta?.title}
				/>
			</div>
				<div>
										{course.Certificate_description}
									</div>
			<div>
				<H2Highlight>{t('courses:common.instructor_team')}</H2Highlight>
				<StaffWrap>
					{course?.instructors?.map((instructor, i) => (
						<StaffComponent
							key={i}
							staff={instructor}></StaffComponent>
					))}
				</StaffWrap>
			</div>
			<CourseButtons course={course} />
			<CourseSummary
				css={css`
					margin-top: 1rem;
				`}>
				<ul>
					<li>
						<FaCalendarCheck />{' '}
						<em>{t('courses:common.next_start date')}</em>{' '}
						<span>
							{nextCohort?.value?.dates?.[0]?.value ??
								nextCohort?.value?.start_date}
						</span>
					</li>
					<li>
						<FaClock /> <em>{t('courses:common.time')}</em>{' '}
						<span>{course.summary?.time}</span>
					</li>
					<li>
						<FaCalendar /> <em>{t('courses:common.duration')}</em>{' '}
						<span>{course.summary?.duration}</span>
					</li>
					<li>
						<FaPortrait /> <em>{t('courses:common.class_size')}</em>{' '}
						<span>{course.summary?.class_size}</span>
					</li>
					<li>
						<FaMapMarkerAlt />{' '}
						<em>{t('courses:common.location')}</em>{' '}
						<span>{course.summary?.location}</span>
					</li>
				</ul>
				<div>
					<GatsbyImage
						css={css`
							${maxWidth[2]} {
								display: none;
							}
						`}
						image={
							course.imageSharp?.childImageSharp?.gatsbyImageData
						}
						alt={course.name}
					/>
				</div>
			</CourseSummary>
			<CoursePRONav course={course} />
		</PROLayout>
	)
}

export default ProIndexPage

export const query = graphql`
	fragment ProCoursePageFragment on Query {
		course: cockpitCoursesCollection(
			_lang: { eq: $language }
			_id: { eq: $id }
		) {
			name
			mode
			code
			zohoCode
			leadValue
			slogan
			description
			outcome
			summary {
				class_size
				location
				duration
				time
			}
			image {
				path
			}
			imageSharp {
				childImageSharp {
					gatsbyImageData(
						width: 400
						height: 400
						placeholder: BLURRED
						transformOptions: { fit: CONTAIN }
						backgroundColor: "transparent"
					)
				}
			}
			Tecky_Academy_MicroMaster_CertificateSharp {
				childImageSharp {
				gatsbyImageData
				}
			}
			Tecky_Academy_MicroMaster_Certificate {
				path
				meta
			}
			TQUK_Certificate {
				path
				meta
			  }
			  TQUK_CertificateSharp {
				childImageSharp {
				  gatsbyImageData
				}
			  }
			Certificate_description
			details {
				title
				mode
				description
				icons {
					path
					meta
				}
				iconsSharp {
					childImageSharp {
						gatsbyImageData(height: 60, placeholder: BLURRED)
					}
				}
				image {
					path
				}
				imageSharp {
					childImageSharp {
						gatsbyImageData(
							width: 400
							height: 400
							placeholder: BLURRED
							transformOptions: { fit: CONTAIN }
							backgroundColor: "transparent"
						)
					}
				}
			}
			details_simple
			leaflet_available
			cohorts {
				value {
					dates {
						value
					}
					class_size
					cohort
					kowloon_bay
					kowloon_bay_full
					kowloon_bay_almost
					sheung_wan
					sheung_wan_almost
					sheung_wan_full
					start_date
					tsuen_wan
					tsuen_wan_almost
					tsuen_wan_full
					uk_online
					uk_online_almost
					uk_online_full
					price
				}
			}
			external_instructors
			instructors {
				name
				description
				title
				specialities
				profileImg {
					path
				}
				available_countries
				profileImgSharp {
					childImageSharp {
						gatsbyImageData(width: 80, placeholder: BLURRED)
					}
				}
			}
		}
	}
	query ProIndex($language: String!, $id: String!) {
		...TranslationFragment
		...ProCoursePageFragment
	}
`
